import DOMPurify from 'dompurify';

export const unCapitalize = (str: string) =>
  typeof str === 'string' ? str.charAt(0).toLowerCase() + str.slice(1) : str;
export function capitalize<T>(str: T) {
  return typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

export const pluralize = (count: number, single: string, plural?: string) =>
  count === 1 ? single : plural || single.concat('s');

export const addProtocolIfMissing = (value: string) => {
  if (!value) return '';
  const result = value.split('://');
  return result.length > 1 ? value : `http://${value}`;
};

export const removeProtocol = (value: string) => {
  if (!value) return '';
  const result = value.split('://');
  return result.length > 1 ? result[1] : value;
};

export const splitFilenameExtension = (value: string) => {
  if (!value) return { filename: undefined, extension: undefined };
  const index = value.lastIndexOf('.');
  if (index < 0) {
    return {
      filename: value,
      extension: undefined,
    };
  }
  const extensionIndex = index + 1;
  return {
    filename: value.substr(0, index),
    extension: extensionIndex < value.length ? value.substr(index + 1) : undefined,
  };
};

export const replaceAll = (target: string, search: string, replacement: string) => {
  if (target == null || target == undefined) {
    return target;
  }
  return target.split(search).join(replacement);
};

export const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function emailIsValid(email: string) {
  return emailRegex.test(email);
}

const uuidRegex = '[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}';
export const validateInviteCode = (code: string) => {
  return RegExp(uuidRegex, 'i').test(code);
};

const URL_PATTERN =
  /(?:(?:https?|ftp):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#\/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#\/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#\/%=~_|$?!:,.]*\)|[A-Z0-9+&@#\/%=~_|$])/gim;

export function insertLinksForUrls(text: string, className = '') {
  const urlPattern = new RegExp(URL_PATTERN);
  const result = text.replace(
    urlPattern,
    `<a class="text-link${className ? ` ${className}` : ''}" href="$&" target="_blank">$&</a>`,
  );

  return result.replace(new RegExp(/href="www./, 'g'), 'href="http://www.');
}

// Workaround for URL's in older comments. Newer comments are rich text, so links are already wrapped in <a> tags,
// whereas older comments are plain text so they need to be wrapped in <a> tags.
export function insertLinksForUrlsWithoutAnchorTags(text: string, className = '') {
  if (text.match(/<a[^>]*?href=[^\s]*.*?<\/a>/gim)) {
    return text;
  } else {
    return insertLinksForUrls(text, className);
  }
}

export function isValidUrl(text: string) {
  return !!text.match(new RegExp(URL_PATTERN));
}

const INTERNAL_DOMAINS = ['crowdworks.it', 'app.crowdworks.it', 'www.crowdworks.it', 'help.crowdworks.it'];
export function isInternalUrl(host: string, url: string) {
  const withoutProtocol = url.replace(/^https?:\/\//, '');
  const domain = decodeURIComponent(withoutProtocol).split('/')[0];
  return domain == host || INTERNAL_DOMAINS.includes(domain);
}

export function truncate(input: string | undefined, maxLength: number): string {
  return maxLength > 3 && input && input.length > maxLength ? input.slice(0, maxLength - 3) + '...' : (input ?? '');
}

export function addPossessive(value: string) {
  if (value.endsWith("'s") || value.endsWith("'")) return value;

  return value.endsWith('s') ? `${value}'` : `${value}'s`;
}

export function isReadableText(text?: string) {
  if (!text) return false;
  const wordCount = text.trim().split(/\s+/).length;

  const strictSymbolsPattern = /[@#%$&*^|~<>[\]{}+\/\\=`;`\n\t]/g;
  const lenientPattern = /[^a-zA-Z0-9 .,?!\s]/g;

  const strictSymbolsCount = (text.match(strictSymbolsPattern) || []).length;
  const nonCommonTextCharCount = (text.match(lenientPattern) || []).length;

  const acceptableSpecialCharRatio = nonCommonTextCharCount < wordCount && strictSymbolsCount < Math.sqrt(wordCount);

  return acceptableSpecialCharRatio;
}

export function camelCaseToSentence(value?: string): string {
  if (!value) {
    return '';
  }
  const sentence = value
    // Insert a space before each uppercase letter (for camelCase)
    .replace(/([A-Z])/g, ' $1')
    // Insert a space between a letter and a following digit
    .replace(/([a-zA-Z])(\d+)/g, '$1 $2')
    // Insert a space between a digit and a following letter
    .replace(/(\d+)([a-zA-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();

  // Capitalize the first letter of the resulting sentence
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * Strips HTML tags from a string while preserving line breaks and structure
 * @param html HTML string to strip
 * @returns Plain text with preserved line breaks
 */
export function stripHtml(html: string): string {
  if (!html) return '';

  // Replace line break elements with actual line breaks
  const processedHtml = html
    .replace(/<br\s*\/?>/gi, '\n') // <br> and <br/> tags
    .replace(/<\/p>/gi, '\n') // End of paragraphs
    .replace(/<\/div>/gi, '\n') // End of divs
    .replace(/<\/h[1-6]>/gi, '\n') // End of headings
    .replace(/<\/li>/gi, '\n') // End of list items
    .replace(/<\/blockquote>/gi, '\n'); // End of blockquotes

  // Strip remaining HTML tags
  const tmp = document.createElement('div');
  tmp.innerHTML = processedHtml;

  // Get text content and clean up multiple line breaks
  const textContent = tmp.textContent || tmp.innerText || '';
  return textContent
    .replace(/\n\s*\n/g, '\n') // Replace multiple line breaks with single ones
    .trim(); // Remove leading/trailing whitespace
}

/**
 * Removes inline styles and unwanted attributes from HTML content
 * This ensures that styled components can control the appearance without interference
 * @param html HTML string to clean
 * @returns Cleaned HTML string without style attributes and unwanted class attributes
 */
export function cleanHtmlStyles(html: string): string {
  if (!html) return '';

  // Define hook function to remove class attributes containing "softbreak"
  const hook = (node: Element, data: { attrName: string; attrValue: string; keepAttr: boolean }) => {
    if (data.attrName === 'class' && data.attrValue && data.attrValue.includes('softbreak')) {
      data.keepAttr = false;
    }
  };

  // Register the hook
  DOMPurify.addHook('uponSanitizeAttribute', hook);

  // Use DOMPurify to sanitize and remove style attributes and unwanted class attributes
  const cleaned = DOMPurify.sanitize(html, {
    FORBID_ATTR: ['style'],
    // Keep all tags and other attributes, but remove style
    // Omitting ALLOWED_TAGS and ALLOWED_ATTR allows all tags and attributes
  });

  // Remove the hook after use to avoid affecting other sanitizations
  DOMPurify.removeHook('uponSanitizeAttribute', hook);

  return cleaned;
}
